import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { ArrowPathIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { useActionsCurrent, useStreamCurrent } from "~/client/hooks";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1)
      ${(props) => props.theme.sizes.modalEdge};
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Details = styled.div`
  margin-right: auto;
  & > p {
    ${(props) => props.theme.styles.textButtonMini}
    & {
      color: ${(props) => props.theme.colors.gray400};
    }
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: ${(props) => props.theme.sizes.edge};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
  }
  ${(props) => props.theme.medias.maxSM} {
    padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
  }
`;

export default function Footer() {
  const { t } = useT();
  const { isLoading, stream, preview } = useStreamCurrent();

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isShort } =
    useActionsCurrent();

  const title = useMemo(() => {
    if (isLoading || _.isNil(stream) || _.isNilOrEmptyString(preview.title)) {
      return _.capitalize(t("words.stream"));
    }

    return `${_.capitalize(t("words.stream"))} ${preview.title}`;
  }, [isLoading, stream, preview, t]);

  const onLoad = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return (
    <Wrapper>
      <Details>
        <p>{t("modalCards.history.footer", { title })}</p>
      </Details>
      <Actions>
        {!isShort &&
          (hasNextPage ? (
            <Button
              accent={"iconic400"}
              appearance={"outline"}
              isLoading={isFetchingNextPage}
              isMini
              left={ArrowPathIcon}
              onClick={onLoad}
              title={t("structs.loadMore")}
            />
          ) : (
            <Button
              accent={"gray400"}
              appearance={"transparent"}
              isMini
              isPreview
              left={CheckIcon}
              title={t("structs.everythingLoaded")}
            />
          ))}
      </Actions>
    </Wrapper>
  );
}
